import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/utils';
import { track } from '../lib/analytics';
import { APP_TIMEZONE } from '../config';
import { useAuth } from '../context/AuthContext';
import CondoLayout from './CondoLayout';

function generateSlots(from, to, durationMinutes) {
  const slots = [];
  const [fh, fm] = from.split(':').map(Number);
  const [th, tm] = to.split(':').map(Number);
  let current = fh * 60 + fm;
  const end = th * 60 + tm;
  while (current + durationMinutes <= end) {
    const h = Math.floor(current / 60);
    const m = current % 60;
    slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    current += durationMinutes;
  }
  return slots;
}

function formatDuration(minutes) {
  if (minutes < 60) return `${minutes}min`;
  const hrs = minutes / 60;
  const display = hrs % 1 === 0 ? hrs : parseFloat(hrs.toFixed(1));
  return `${display} hr`;
}

function formatTime(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:${String(m).padStart(2, '0')} ${period}`;
}

function currentTimeString() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(new Date());
  const h = parts.find(p => p.type === 'hour').value;
  const m = parts.find(p => p.type === 'minute').value;
  return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
}

function todayString() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: APP_TIMEZONE }).format(new Date());
}

function maxDateString(advanceBookingDays) {
  const d = new Date();
  d.setDate(d.getDate() + advanceBookingDays);
  return new Intl.DateTimeFormat('en-CA', { timeZone: APP_TIMEZONE }).format(d);
}

export function AmenityFormModal({ amenity, condoId, onSave, onClose, getToken }) {
  const isEdit = !!amenity;
  const [form, setForm] = useState({
    name: amenity?.name || '',
    description: amenity?.description || '',
    maxCapacity: amenity?.maxCapacity ?? 1,
    bookingDurationMinutes: amenity?.bookingDurationMinutes ?? 60,
    availableFrom: amenity?.availableFrom || '08:00',
    availableTo: amenity?.availableTo || '20:00',
    advanceBookingDays: amenity?.advanceBookingDays ?? 14,
    isActive: amenity?.isActive ?? true,
    maxBookingsPerResident: amenity?.maxBookingsPerResident ?? '',
  });
  const [rules, setRules] = useState(amenity?.rules || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleRuleChange = (index, value) => {
    setRules(prev => prev.map((r, i) => i === index ? value : r));
  };

  const handleAddRule = () => {
    setRules(prev => [...prev, '']);
  };

  const handleRemoveRule = (index) => {
    setRules(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...form,
        maxCapacity: Number(form.maxCapacity),
        bookingDurationMinutes: Number(form.bookingDurationMinutes),
        advanceBookingDays: Number(form.advanceBookingDays),
        rules: rules.filter(r => r.trim() !== ''),
        maxBookingsPerResident: form.maxBookingsPerResident !== '' ? Number(form.maxBookingsPerResident) : null,
      };
      if (isEdit) {
        await axios.put(`${getApiBaseUrl()}/api/amenities/${amenity._id}`, payload, { headers });
      } else {
        await axios.post(`${getApiBaseUrl()}/api/amenities`, { ...payload, condoId }, { headers });
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save amenity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">
            {isEdit ? 'Edit Amenity' : 'Add Amenity'}
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {error && (
            <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="amenity-name" className="block text-sm font-medium text-foreground mb-1">Name *</label>
            <input
              id="amenity-name"
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              maxLength={100}
              className="w-full px-3 py-2 bg-secondary/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          <div>
            <label htmlFor="amenity-description" className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea
              id="amenity-description"
              name="description"
              value={form.description}
              onChange={handleChange}
              maxLength={500}
              rows={3}
              className="w-full px-3 py-2 bg-secondary/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="amenity-capacity" className="block text-sm font-medium text-foreground mb-1">Max Capacity *</label>
              <input
                id="amenity-capacity"
                type="number"
                name="maxCapacity"
                value={form.maxCapacity}
                onChange={handleChange}
                required
                min={1}
                className="w-full px-3 py-2 bg-secondary/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
            <div>
              <label htmlFor="amenity-duration" className="block text-sm font-medium text-foreground mb-1">Slot Duration (min) *</label>
              <input
                id="amenity-duration"
                type="number"
                name="bookingDurationMinutes"
                value={form.bookingDurationMinutes}
                onChange={handleChange}
                required
                min={15}
                step={15}
                className="w-full px-3 py-2 bg-secondary/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="amenity-from" className="block text-sm font-medium text-foreground mb-1">Open From *</label>
              <input
                id="amenity-from"
                type="time"
                name="availableFrom"
                value={form.availableFrom}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 bg-secondary/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
            <div>
              <label htmlFor="amenity-to" className="block text-sm font-medium text-foreground mb-1">Close At *</label>
              <input
                id="amenity-to"
                type="time"
                name="availableTo"
                value={form.availableTo}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 bg-secondary/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="amenity-advance" className="block text-sm font-medium text-foreground mb-1">Advance Booking Days *</label>
              <input
                id="amenity-advance"
                type="number"
                name="advanceBookingDays"
                value={form.advanceBookingDays}
                onChange={handleChange}
                required
                min={1}
                className="w-full px-3 py-2 bg-secondary/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
            <div>
              <label htmlFor="amenity-max-bookings" className="block text-sm font-medium text-foreground mb-1">Max Bookings / Resident</label>
              <input
                id="amenity-max-bookings"
                type="number"
                name="maxBookingsPerResident"
                value={form.maxBookingsPerResident}
                onChange={handleChange}
                min={1}
                placeholder="Unlimited"
                className="w-full px-3 py-2 bg-secondary/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
          </div>

          {isEdit && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="isActive"
                id="isActive"
                checked={form.isActive}
                onChange={handleChange}
                className="w-4 h-4 rounded border-border"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-foreground">Active</label>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-foreground">Booking Rules</label>
              <button
                type="button"
                onClick={handleAddRule}
                className="text-xs text-primary hover:text-primary/80 font-medium"
              >
                + Add Rule
              </button>
            </div>
            {rules.length === 0 && (
              <p className="text-xs text-muted-foreground">No rules. Residents will not be prompted before booking.</p>
            )}
            <div className="space-y-2">
              {rules.map((rule, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <textarea
                    value={rule}
                    onChange={e => handleRuleChange(i, e.target.value)}
                    maxLength={5000}
                    rows={3}
                    placeholder={`Rule ${i + 1}`}
                    className="flex-1 px-3 py-2 bg-secondary/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary resize-none"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveRule(i)}
                    className="text-muted-foreground hover:text-destructive mt-1 flex-shrink-0"
                    aria-label="Remove rule"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-primary text-primary-foreground py-2 rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Amenity'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-secondary text-secondary-foreground py-2 rounded-lg font-medium hover:bg-secondary/80 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RulesAcknowledgementModal({ rules, onConfirm, onClose }) {
  const [checked, setChecked] = useState(rules.map(() => false));
  const allChecked = checked.every(Boolean);

  const toggle = (i) => setChecked(prev => prev.map((v, idx) => idx === i ? !v : v));

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-foreground">Booking Rules</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
          <p className="text-sm text-muted-foreground">Please read and acknowledge all rules before confirming your booking.</p>
          {rules.map((rule, i) => (
            <label key={i} htmlFor={`rule-${i}`} className="flex gap-3 items-center cursor-pointer group">
              <input
                type="checkbox"
                id={`rule-${i}`}
                checked={checked[i]}
                onChange={() => toggle(i)}
                className="sr-only peer"
              />
              <span className="flex-shrink-0 w-5 h-5 rounded border-2 border-border bg-secondary/50 flex items-center justify-center transition-colors peer-checked:bg-primary peer-checked:border-primary group-hover:border-primary/60">
                {checked[i] && (
                  <svg className="w-3 h-3 text-primary-foreground" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span className="text-sm text-foreground whitespace-pre-wrap">{rule}</span>
            </label>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-border flex gap-3 flex-shrink-0">
          <button
            onClick={onConfirm}
            disabled={!allChecked}
            className="flex-1 bg-primary text-primary-foreground py-2 rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            Confirm Booking
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-secondary text-secondary-foreground py-2 rounded-lg font-medium hover:bg-secondary/80 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function BookingPanel({ amenity, user, getToken, onBookingChange, refreshKey }) {
  const [date, setDate] = useState(todayString());
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingAction, setBookingAction] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [pendingBookingSlot, setPendingBookingSlot] = useState(null);
  const [userBookingsInfo, setUserBookingsInfo] = useState({ count: 0, limit: null });

  const fetchUserBookings = useCallback(async () => {
    try {
      const token = getToken();
      const res = await axios.get(
        `${getApiBaseUrl()}/api/amenities/${amenity._id}/bookings/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserBookingsInfo({ count: res.data.bookings.length, limit: res.data.limit });
    } catch {
      // non-critical
    }
  }, [amenity._id, getToken]);

  const fetchBookings = useCallback(async () => {
    setLoadingBookings(true);
    setActionError(null);
    try {
      const token = getToken();
      const res = await axios.get(
        `${getApiBaseUrl()}/api/amenities/${amenity._id}/bookings?date=${date}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookings(res.data.bookings || []);
    } catch (err) {
      setBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  }, [amenity._id, date, getToken]);

  useEffect(() => {
    fetchBookings();
    fetchUserBookings();
  }, [fetchBookings, fetchUserBookings]);

  useEffect(() => {
    if (refreshKey > 0) {
      fetchBookings();
      fetchUserBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const confirmBook = async (startTime) => {
    setBookingAction(startTime);
    setActionError(null);
    try {
      const token = getToken();
      await axios.post(
        `${getApiBaseUrl()}/api/amenities/${amenity._id}/bookings`,
        { date, startTime },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      track('amenity_booked');
      await Promise.all([fetchBookings(), fetchUserBookings()]);
      onBookingChange();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to book slot');
    } finally {
      setBookingAction(null);
    }
  };

  const handleBook = (startTime) => {
    const activeRules = amenity.rules?.filter(r => r.trim() !== '') || [];
    if (activeRules.length > 0) {
      setPendingBookingSlot(startTime);
    } else {
      confirmBook(startTime);
    }
  };

  const handleCancel = async (bookingId) => {
    setBookingAction(bookingId);
    setActionError(null);
    try {
      const token = getToken();
      await axios.delete(
        `${getApiBaseUrl()}/api/amenities/${amenity._id}/bookings/${bookingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await Promise.all([fetchBookings(), fetchUserBookings()]);
      onBookingChange();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to cancel booking');
    } finally {
      setBookingAction(null);
    }
  };

  const isAtLimit = userBookingsInfo.limit !== null && userBookingsInfo.count >= userBookingsInfo.limit;

  const isToday = date === todayString();
  const nowTime = isToday ? currentTimeString() : null;
  const slots = generateSlots(amenity.availableFrom, amenity.availableTo, amenity.bookingDurationMinutes).filter(slot => {
    if (!isToday) return true;
    const [sh, sm] = slot.split(':').map(Number);
    const endMin = sh * 60 + sm + amenity.bookingDurationMinutes;
    const endSlot = `${String(Math.floor(endMin / 60)).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
    return endSlot > nowTime;
  });

  const bookingsBySlot = {};
  bookings.forEach(b => {
    if (!bookingsBySlot[b.startTime]) bookingsBySlot[b.startTime] = [];
    bookingsBySlot[b.startTime].push(b);
  });

  const activeRules = amenity.rules?.filter(r => r.trim() !== '') || [];

  return (
    <div className="px-4 pb-4 md:px-6 md:pb-6 border-t border-border">
      {pendingBookingSlot && (
        <RulesAcknowledgementModal
          rules={activeRules}
          onConfirm={() => {
            setPendingBookingSlot(null);
            confirmBook(pendingBookingSlot);
          }}
          onClose={() => setPendingBookingSlot(null)}
        />
      )}
      {userBookingsInfo.limit !== null && userBookingsInfo.count >= userBookingsInfo.limit && (
        <div className="px-3 py-2 rounded text-sm mt-4 mb-4 bg-secondary/50 border border-border text-muted-foreground">
          You've reached your maximum active bookings for this amenity
        </div>
      )}
      <div className="pt-4 mb-4">
        <label className="block text-sm font-medium text-foreground mb-1">Select Date</label>
        <input
          type="date"
          value={date}
          min={todayString()}
          max={maxDateString(amenity.advanceBookingDays)}
          onChange={e => setDate(e.target.value)}
          className="px-3 py-2 bg-secondary/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
        />
      </div>
      {actionError && (
        <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-3 py-2 rounded text-sm mb-4">
          {actionError}
        </div>
      )}

      {loadingBookings ? (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      ) : slots.length === 0 ? (
        <p className="text-sm text-muted-foreground">No available time slots for this amenity.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {slots.map(slot => {
            const slotBookings = bookingsBySlot[slot] || [];
            const userBooking = slotBookings.find(b => b.user?._id?.toString() === user?._id?.toString());
            const spotsUsed = slotBookings.length;
            const spotsLeft = amenity.maxCapacity - spotsUsed;
            const isFull = spotsLeft <= 0 && !userBooking;
            const isActing = bookingAction === slot || bookingAction === userBooking?._id;

            const [sh, sm] = slot.split(':').map(Number);
            const endMin = sh * 60 + sm + amenity.bookingDurationMinutes;
            const endSlot = `${String(Math.floor(endMin / 60)).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;

            return (
              <div
                key={slot}
                className={`border rounded-lg p-3 text-sm ${
                  userBooking
                    ? 'border-primary bg-primary/10'
                    : isFull
                    ? 'border-border bg-secondary/30 opacity-60'
                    : 'border-border bg-card hover:bg-secondary/30'
                }`}
              >
                <div className="font-medium text-foreground mb-2">{formatTime(slot)} — {formatTime(endSlot)}</div>
                {userBooking ? (
                  <>
                    <div className="text-xs text-primary font-medium mb-2">Your booking</div>
                    <button
                      onClick={() => handleCancel(userBooking._id)}
                      disabled={isActing}
                      className="w-full text-xs py-1 px-2 rounded bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 transition-colors"
                    >
                      {isActing ? '...' : 'Cancel'}
                    </button>
                  </>
                ) : isFull ? (
                  <div className="text-xs text-muted-foreground">Full</div>
                ) : isAtLimit ? (
                  <>
                    <div className="text-xs text-muted-foreground mb-2">
                      {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} left
                    </div>
                    <div className="text-xs text-muted-foreground">Limit reached</div>
                  </>
                ) : (
                  <>
                    <div className="text-xs text-muted-foreground mb-2">
                      {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} left
                    </div>
                    <button
                      onClick={() => handleBook(slot)}
                      disabled={isActing}
                      className="w-full text-xs py-1 px-2 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
                    >
                      {isActing ? '...' : 'Book'}
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MyBookingsSection({ condoId, getToken, refreshKey, onBookingChange }) {
  const [expanded, setExpanded] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [error, setError] = useState(null);

  const fetchMyBookings = useCallback(async () => {
    try {
      const token = getToken();
      const res = await axios.get(
        `${getApiBaseUrl()}/api/amenities/my-bookings?condoId=${condoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookings(res.data.bookings || []);
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, [condoId, getToken]);

  useEffect(() => {
    fetchMyBookings();
  }, [fetchMyBookings]);

  useEffect(() => {
    if (refreshKey > 0) fetchMyBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleCancel = async (booking) => {
    setCancellingId(booking._id);
    setError(null);
    try {
      const token = getToken();
      await axios.delete(
        `${getApiBaseUrl()}/api/amenities/${booking.amenity._id}/bookings/${booking._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookings(prev => prev.filter(b => b._id !== booking._id));
      onBookingChange();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel booking');
    } finally {
      setCancellingId(null);
    }
  };

  const formatDate = (isoDate) => {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC'
    }).format(new Date(isoDate));
  };

  const count = bookings.length;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden mb-4">
      <button
        onClick={() => setExpanded(prev => !prev)}
        className="w-full px-4 py-4 md:px-6 md:py-5 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
      >
        <span className="text-base md:text-lg font-semibold text-foreground">
          My Bookings{!loading && count > 0 ? ` (${count})` : ''}
        </span>
        <span className="text-sm text-muted-foreground flex-shrink-0 ml-4">
          {expanded ? '▼' : '▶'}
        </span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 md:px-6 md:pb-6 border-t border-border pt-4">
          {error && (
            <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-3 py-2 rounded text-sm mb-4">
              {error}
            </div>
          )}
          {loading ? (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : count === 0 ? (
            <p className="text-sm text-muted-foreground">You have nothing booked currently.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {bookings.map(b => (
                <div key={b._id} className="border border-primary bg-primary/10 rounded-lg p-3 text-sm">
                  <div className="font-medium text-foreground mb-1">{b.amenity.name}</div>
                  <div className="text-xs text-muted-foreground mb-1">{formatDate(b.date)}</div>
                  <div className="text-xs text-muted-foreground mb-2">{formatTime(b.startTime)} — {formatTime(b.endTime)}</div>
                  <button
                    onClick={() => handleCancel(b)}
                    disabled={cancellingId === b._id}
                    className="w-full text-xs py-1 px-2 rounded bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 transition-colors"
                  >
                    {cancellingId === b._id ? '...' : 'Cancel'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AmenitiesContent({ condo }) {
  const { getToken, user } = useAuth();
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState([]);
  const [bookingsVersion, setBookingsVersion] = useState(0);

  const fetchAmenities = useCallback(async () => {
    if (!condo?._id) return;
    setLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${getApiBaseUrl()}/api/amenities/condo/${condo._id}`, { headers: { Authorization: `Bearer ${token}` } });
      const fetchedAmenities = res.data.amenities || [];
      setAmenities(fetchedAmenities);
      setExpandedIds(fetchedAmenities.map(a => a._id));
      setError(null);
    } catch (err) {
      setError('Failed to load amenities. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [condo, getToken]);

  useEffect(() => {
    fetchAmenities();
  }, [fetchAmenities]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="mt-4 text-muted-foreground">Loading amenities...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <MyBookingsSection condoId={condo._id} getToken={getToken} refreshKey={bookingsVersion} onBookingChange={() => setBookingsVersion(v => v + 1)} />

      {amenities.length === 0 && !error ? (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <div className="mb-4 text-muted-foreground flex justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <p className="text-foreground text-base md:text-lg font-semibold">No amenities available</p>
          <p className="text-muted-foreground text-sm md:text-base mt-2">
            Check back later for available amenities.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {amenities.map(a => (
            <div key={a._id} className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedIds(prev => 
  prev.includes(a._id) 
    ? prev.filter(id => id !== a._id)
    : [...prev, a._id]
)}
                className="w-full px-4 py-4 md:px-6 md:py-5 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
              >
                <div>
                  <span className="text-base md:text-lg font-semibold text-foreground">{a.name}</span>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                    <span className="text-xs text-muted-foreground">
                      {formatTime(a.availableFrom)} – {formatTime(a.availableTo)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDuration(a.bookingDurationMinutes)} slots
                    </span>
                  </div>
                  {a.description && (
                    <p className="text-sm text-muted-foreground mt-1">{a.description}</p>
                  )}
                </div>
                <span className="text-sm text-muted-foreground flex-shrink-0 ml-4">
                  {expandedIds.includes(a._id) ? '▼' : '▶'}
                </span>
              </button>

              {expandedIds.includes(a._id) && (
                <BookingPanel
                  amenity={a}
                  user={user}
                  getToken={getToken}
                  onBookingChange={() => setBookingsVersion(v => v + 1)}
                  refreshKey={bookingsVersion}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {amenities.length > 0 && (
        <div className="mt-6 md:mt-8 text-center text-xs md:text-sm text-muted-foreground">
          {amenities.length} {amenities.length === 1 ? 'amenity' : 'amenities'}
        </div>
      )}

    </div>
  );
}

function AmenitiesPage() {
  return (
    <CondoLayout>
      {({ condo }) => <AmenitiesContent condo={condo} />}
    </CondoLayout>
  );
}

export default AmenitiesPage;
